import { DEVISE, fmt } from './currency';
import { localeDates, tr, trf } from './i18n';
import { jourIso } from './rapprochement-sorties';

/** « 2026-01-29 » ou « 29.01.2026 » → « 29.01.2026 ». */
const jourSuisse = (d) => {
  const iso = jourIso(d);
  return iso ? iso.slice(8, 10) + '.' + iso.slice(5, 7) + '.' + iso.slice(0, 4) : '';
};

/** Les fréquences d'une dépense qui revient, dites comme sur la page Dépenses. */
const FREQUENCES = {
  mensuel: 'Chaque mois',
  trimestriel: 'Chaque trimestre',
  semestriel: 'Chaque semestre',
  annuel: 'Une fois par année',
};

/** Le texte du bulletin lu à la place du nom (« À r avant de procédérau paiement »). */
const TEXTE_DU_BULLETIN =
  /(avant de proc|d[ée]tacher|vor der einzahlung|abzutrennen|prima del versamento|staccare|r[ée]c[ée]piss[ée]|empfangsschein|zahlteil|section paiement)/i;

/**
 * Le nom d'un fournisseur sans son adresse (« Nom — rue »), pour regrouper.
 * Quand c'est le texte du bulletin qui a été lu à la place du nom, c'est
 * l'adresse qui distingue les créanciers : deux bailleurs lus « À détacher
 * avant de procéder au paiement » ne font pas un seul groupe. Toutes les
 * écritures comptent (« Обед » et « Бензин » ne se réduisent pas à « 1 ») ;
 * une clé sans aucune lettre ne regroupe rien.
 */
const cleDuNom = (x) => {
  const [tete, ...reste] = String(x.fournisseur || x.libelle || '').split(' — ');
  const nom = TEXTE_DU_BULLETIN.test(tete) ? reste.join(' — ') : tete;
  const cle = nom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return /\p{L}/u.test(cle) ? cle : '';
};

/**
 * Le nom d'un fournisseur tel qu'il s'affiche : quand c'est le texte du
 * bulletin qui a été lu à sa place, seule son adresse reste, en attendant le
 * nom lu dans le code QR.
 *
 * @param {string} nom nom enregistré (« Nom — adresse »)
 * @returns {string} le nom à afficher
 */
export function sansTexteDuBulletin(nom) {
  const [tete, ...reste] = String(nom || '').split(' — ');
  if (!TEXTE_DU_BULLETIN.test(tete)) return String(nom || '');
  return reste
    .join(' — ')
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .trim();
}

/** « janvier 2026 », dans la langue de l'écran. */
const moisLisible = (d) => {
  const iso = jourIso(d);
  if (!iso) return '';
  const texte = new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, 1).toLocaleDateString(
    localeDates(),
    { month: 'long', year: 'numeric' },
  );
  return texte.charAt(0).toUpperCase() + texte.slice(1);
};

/**
 * D'où vient le jour « Payé le » d'une dépense : trouvé dans un relevé de
 * banque importé (une sortie du même jour et du même montant), ou noté à la
 * main ; rien quand elle n'est pas payée.
 *
 * @param {object} x dépense ({payeLe, montant})
 * @param {object[]} [releve] mouvements gardés ({date, montant, sens})
 * @returns {''|'releve'|'main'} la source
 */
export function sourceDuPaiement(x, releve) {
  const jour = jourIso(x?.payeLe);
  if (!jour) return '';
  const montant = Math.abs(+x.montant || 0);
  const trouve = (releve || []).some(
    (t) =>
      // Une sortie seulement : ni une entrée, ni un sens « à vérifier ».
      !/^(crdt|credit|cr[ée]dit|entr|\?)/i.test(String(t?.sens || '')) &&
      jourIso(t?.date) === jour &&
      Math.abs(Math.abs(+t?.montant || 0) - montant) < 0.005,
  );
  return trouve ? 'releve' : 'main';
}

/**
 * Les paiements d'un même fournisseur qui revient (le loyer du dépôt chaque
 * mois, une assurance, un leasing), prêts pour la feuille A4 : un groupe par
 * série — ou par fournisseur quand la dépense n'a pas de série —, seulement
 * ceux payés au moins deux fois.
 *
 * @param {object[]} items pièces d'un type du classeur ({id, date, montant,
 *   fournisseur, libelle, payeLe, serie, frequence, origine})
 * @param {object[]} [releve] mouvements de banque gardés
 * @returns {object[]} les groupes ({cle, fournisseur, adresse, frequence,
 *   lignes, total, paye, reste})
 */
export function groupesDePaiements(items, releve) {
  const parCle = new Map();
  (items || [])
    .filter((x) => typeof x.id === 'number' && (x.origine === undefined || x.origine === 'frais'))
    .forEach((x) => {
      const cle = x.serie ? 'serie:' + x.serie : 'nom:' + cleDuNom(x);
      if (cle === 'nom:') return;
      if (!parCle.has(cle)) parCle.set(cle, []);
      parCle.get(cle).push(x);
    });
  return [...parCle.entries()]
    .filter(([, liste]) => liste.length >= 2)
    .map(([cle, liste]) => {
      const tries = [...liste].sort(
        (a, b) => jourIso(a.date).localeCompare(jourIso(b.date)) || a.id - b.id,
      );
      // Le nom le plus récent : celui des mois suivants, corrigé s'il l'a été.
      const recent = tries[tries.length - 1];
      const [nom, ...adresse] = String(recent.fournisseur || recent.libelle || '').split(' — ');
      const lisible = TEXTE_DU_BULLETIN.test(nom || '') ? '' : nom;
      const total = tries.reduce((s, x) => s + (+x.montant || 0), 0);
      const paye = tries.filter((x) => x.payeLe).reduce((s, x) => s + (+x.montant || 0), 0);
      const frequence = FREQUENCES[String(recent.frequence || '').toLowerCase()];
      return {
        cle,
        fournisseur: (lisible || '').trim(),
        adresse: adresse
          .join(' — ')
          .replace(/^[^\p{L}\p{N}]+/u, '')
          .trim(),
        frequence: frequence ? tr(frequence) : '',
        lignes: tries.map((x) => ({
          id: x.id,
          libelle: moisLisible(x.date),
          echeance: jourSuisse(x.date),
          payeLe: jourSuisse(x.payeLe),
          source: sourceDuPaiement(x, releve),
          montant: fmt(+x.montant || 0),
        })),
        total: fmt(total),
        paye: fmt(paye),
        reste: fmt(total - paye),
      };
    });
}

/**
 * Ce que le serveur reçoit pour la feuille : les groupes et les textes dans la
 * langue de l'écran.
 *
 * @param {object} p choix
 * @param {string} p.type nom du type de dépense (« Location, bail »)
 * @param {string} [p.compte] compte du plan comptable (« 6000 »)
 * @param {string} [p.periode] année (« 2026 »), ou rien
 * @param {object} [p.soc] entreprise
 * @param {object[]} p.groupes groupes de {@link groupesDePaiements}
 * @returns {object} la demande
 */
export function demandePaiementsA4({ type, compte, periode, soc, groupes }) {
  return {
    societe: soc?.nom || '',
    titre: trf('{0} — paiements {1}', tr(type), periode || '').trim(),
    periode: periode || '',
    compte: compte || '',
    devise: DEVISE,
    libelles: {
      compte: tr('Compte'),
      etabli: trf('établi le {0}', '').trim(),
      payeA: tr('Bénéficiaire'),
      inconnu: tr('Fournisseur'),
      paiement: tr('Paiement'),
      nombre: tr('Paiements'),
      total: tr('Total'),
      dejaPaye: tr('Déjà payé'),
      reste: tr('Reste à payer'),
      colQuoi: tr('Mois'),
      colEcheance: tr('Échéance'),
      colPayeLe: tr('Payé le'),
      colMontant: tr('Montant'),
      releve: tr('relevé bancaire'),
      main: tr('noté à la main'),
      attente: tr('Pas encore payé'),
      ligneTotal: tr('Total'),
      bulletin: tr('Bulletin de versement'),
      origine: tr('D’où vient la date « Payé le » :'),
      origineReleve: tr('trouvée toute seule dans le relevé importé (même jour, même montant)'),
      origineMain: tr('tapée dans SwissTech quand le paiement n’est pas dans un relevé'),
      origineAttente: tr('aucun paiement trouvé : à vérifier'),
      suite: tr('suite'),
      page: tr('Page'),
    },
    groupes: groupes.map((g) => ({
      fournisseur: g.fournisseur,
      adresse: g.adresse,
      frequence: g.frequence,
      total: g.total,
      paye: g.paye,
      reste: g.reste,
      lignes: g.lignes,
    })),
  };
}
